/**
 * Implementation based off George's example python code
 * https://app.clickup.com/t/86b14atzn
 */

import * as unicode from "unicode-properties";

// Create list containing single-quote-like characters
const squotesCodes = [0x0027, 0x2018, 0x2019, 0x201a, 0x201b, 0x2039, 0x203a];
const squotes = Array.from(squotesCodes.map((codePoint) => String.fromCodePoint(codePoint)));

// Create list containing double-quote-like characters
const dquotesCodes = [0x0022, 0x00ab, 0x00bb, 0x201c, 0x201d, 0x201e, 0x201f, 0x2e42];
const dquotes = Array.from(dquotesCodes.map((codePoint) => String.fromCodePoint(codePoint)));

// Create list containing whitespace
const whitespaceCodes = [
  0x0009, 0x000a, 0x000b, 0x000c, 0x000d, 0x0020, 0x0085, 0x00a0, 0x1680, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004,
  0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
];
const whitespace = Array.from(whitespaceCodes.map((codePoint) => String.fromCodePoint(codePoint)));

const isWhitespace = (c: string) => {
  return whitespace.includes(c);
  // using unicode category would require multiple category checks
  // return unicode.getCategory(char) === "Zs";
};

const isSingleQuote = (c: string) => {
  return squotes.includes(c);
};

const isDoubleQuote = (c: string) => {
  return dquotes.includes(c);
};

// Non-spacing marks
const isNonSpacingMark = (char: number) => {
  return unicode.getCategory(char) === "Mn";
};

// Dash punctuation
const isDash = (char: number) => {
  return unicode.getCategory(char) === "Pd";
};

// Letters
const isLetter = (char: number) => {
  return unicode.getCategory(char)[0] === "L";
};

// Numbers
const isNumber = (char: number) => {
  return unicode.getCategory(char)[0] === "N";
};

export const stripDiacritics = (str: string) => {
  if (!str) return "";
  const normalizedStr = str.normalize("NFD");
  return Array.from(normalizedStr)
    .filter((char) => {
      const code = char.codePointAt(0);
      return code && !isNonSpacingMark(code);
    })
    .join("");
};

// character mapping
const mapChar = (c: string) => {
  const code = c.codePointAt(0);
  if (code === undefined) return c;
  if (c === "|") return "\\";
  if (isWhitespace(c)) return " ";
  if (isSingleQuote(c)) return "'";
  if (isDoubleQuote(c)) return '"';
  if (isDash(code)) return "-";
  return c;
};

/**
 * Normalise text by removing diacritics, trimming leading and trailing whitespace,
 * and normalising inner whitespace and dashes.
 */
export const normaliseText = (str: string) => {
  if (!str) return "";
  // Remove diacritics
  const normalized = stripDiacritics(str);
  // Find leading and trailing whitespace
  let start = 0;
  while (start < normalized.length && isWhitespace(normalized[start])) {
    start++;
  }
  let end = normalized.length;
  while (end > start && isWhitespace(normalized[end - 1])) {
    end--;
  }

  // Handle empty string
  if (start === end) return "";

  // normalise inner string
  let text = mapChar(normalized[start]); // map and copy first char
  let prev = text[0]; // setup previous char
  for (let i = start + 1; i < end; i++) {
    // loop over remaining chars of the inner string
    const curr = mapChar(normalized[i]); // map current char
    // skip consecutive whitespace or dashes
    if ((curr === " " && prev === " ") || (curr === "-" && prev === "-")) {
      continue;
    }
    text += curr;
    prev = curr;
  }
  return text;
};
